import { useAtomValue } from 'jotai';
import type { Expression } from 'mapbox-gl';
import { Layer, Source } from 'react-map-gl';
import {
  wellsV2HorizontalLayerAtom,
  wellsV2HorizontalProducingLayerAtom,
  wellsV2HorizontalDucLayerAtom,
  wellsV2HorizontalPermitLayerAtom,
  wellsV2HorizontalShutInLayerAtom,
  wellsV2HorizontalExpiredPermitLayerAtom,
  wellsV2HorizontalInactiveLayerAtom,
  wellsV2HorizontalNonDerivedEnabledAtom,
  wellsV2VerticalLayerAtom,
  wellsV2VerticalProducingLayerAtom,
  wellsV2VerticalDucLayerAtom,
  wellsV2VerticalPermitLayerAtom,
  wellsV2VerticalShutInLayerAtom,
  wellsV2VerticalExpiredPermitLayerAtom,
  wellsV2VerticalInactiveLayerAtom,
  wellsV2VerticalNonDerivedEnabledAtom,
} from './atoms';
import { WELL_STATUS_COLOR_MAP } from 'utils/well-status';
import {
  prePermitHexagonLayout,
  prePermitHexagonPaint,
  usePrePermitHexagonImage,
} from 'components/map/pre-permit-hexagon';

// Define props interface for consistency with other scoped layers
interface Props {
  source?: string;
}

const colors = {
  producing: '#4292c6', // Blue for producing
  duc: '#fe9929', // Orange for DUC
  permit: '#ff69b4', // Pink for permit
  shutIn: '#555555', // Darker gray for shut in
  expiredPermit: '#969696', // Gray for expired permit
  inactive: '#cccccc', // Lighter gray for inactive
  // The four Create New Well statuses, from the shared colour file (INS-13300).
  proposal: WELL_STATUS_COLOR_MAP.proposal,
  planned: WELL_STATUS_COLOR_MAP.planned,
  prePermit: WELL_STATUS_COLOR_MAP.pre_permit,
  apd: WELL_STATUS_COLOR_MAP.apd,
};

/**
 * One `==` status condition per Create New Well status switched on. Each has its own toggle
 * (INS-13300), so a direction draws only those, each in its own colour.
 *
 * @param {string[]} statuses - The statuses switched on, for one direction.
 * @return {Array} The filter conditions.
 */
const statusFilters = (statuses: readonly string[]) =>
  statuses.map((status) => ['==', 'status', status]);
const PRE_PERMIT_COLOR: Expression = [
  'match',
  ['get', 'status'],
  'proposal',
  colors.proposal,
  'planned',
  colors.planned,
  'pre_permit',
  colors.prePermit,
  'apd',
  colors.apd,
  colors.prePermit,
] as Expression;

const ScopedPackageWellsV2Layer = ({ source }: Props) => {
  // The Create New Well statuses draw as one hollow hexagon (INS-13300).
  usePrePermitHexagonImage();

  // Horizontal category and status atoms
  const isHorizontalEnabled =
    useAtomValue(wellsV2HorizontalLayerAtom) !== 'UNCHECKED';
  const isHorizontalProducingEnabled = useAtomValue(
    wellsV2HorizontalProducingLayerAtom
  );
  const isHorizontalDucEnabled = useAtomValue(wellsV2HorizontalDucLayerAtom);
  const isHorizontalPermitEnabled = useAtomValue(
    wellsV2HorizontalPermitLayerAtom
  );
  const isHorizontalShutInEnabled = useAtomValue(
    wellsV2HorizontalShutInLayerAtom
  );
  const isHorizontalExpiredPermitEnabled = useAtomValue(
    wellsV2HorizontalExpiredPermitLayerAtom
  );
  const isHorizontalInactiveEnabled = useAtomValue(
    wellsV2HorizontalInactiveLayerAtom
  );
  const horizontalNonDerived = useAtomValue(
    wellsV2HorizontalNonDerivedEnabledAtom
  );

  // Vertical category and status atoms
  const isVerticalEnabled =
    useAtomValue(wellsV2VerticalLayerAtom) !== 'UNCHECKED';
  const isVerticalProducingEnabled = useAtomValue(
    wellsV2VerticalProducingLayerAtom
  );
  const isVerticalDucEnabled = useAtomValue(wellsV2VerticalDucLayerAtom);
  const isVerticalPermitEnabled = useAtomValue(wellsV2VerticalPermitLayerAtom);
  const isVerticalShutInEnabled = useAtomValue(wellsV2VerticalShutInLayerAtom);
  const isVerticalExpiredPermitEnabled = useAtomValue(
    wellsV2VerticalExpiredPermitLayerAtom
  );
  const isVerticalInactiveEnabled = useAtomValue(
    wellsV2VerticalInactiveLayerAtom
  );
  const verticalNonDerived = useAtomValue(wellsV2VerticalNonDerivedEnabledAtom);

  return (
    <Source id="package-wells-v2-layer-source" type="vector" url={source}>
      {/* HORIZONTAL WELL LAYERS */}
      {isHorizontalEnabled && (
        <>
          <Layer
            id="package-wells-v2-horizontal-survey"
            source="package-wells-v2-layer-source"
            type="line"
            source-layer="survey"
            filter={[
              'all',
              [
                'in',
                'hole_direction',
                'HORIZONTAL',
                'HORIZONTAL MINING BOREHOLE',
              ],
              [
                'any',
                ...(isHorizontalProducingEnabled
                  ? [['==', 'status', 'producing']]
                  : []),
                ...(isHorizontalDucEnabled ? [['==', 'status', 'duc']] : []),
                ...(isHorizontalPermitEnabled
                  ? [['==', 'status', 'permit']]
                  : []),
                ...(isHorizontalShutInEnabled
                  ? [['==', 'status', 'shut_in']]
                  : []),
                ...(isHorizontalExpiredPermitEnabled
                  ? [['==', 'status', 'expired_permit']]
                  : []),
                ...(isHorizontalInactiveEnabled
                  ? [['==', 'status', 'inactive']]
                  : []),
                ...statusFilters(horizontalNonDerived),
              ],
            ]}
            paint={{
              'line-color': [
                'match',
                ['get', 'status'],
                'producing',
                colors.producing,
                'duc',
                colors.duc,
                'permit',
                colors.permit,
                'shut_in',
                colors.shutIn,
                'expired_permit',
                colors.expiredPermit,
                'inactive',
                colors.inactive,
                'proposal',
                colors.proposal,
                'planned',
                colors.planned,
                'pre_permit',
                colors.prePermit,
                'apd',
                colors.apd,
                '#000000', // default color if none match
              ],
              'line-width': 1.5,
            }}
          />

          {/* Well names along survey lines for horizontal wells */}
          <Layer
            id="package-wells-v2-horizontal-survey-names"
            source="package-wells-v2-layer-source"
            type="symbol"
            source-layer="survey"
            filter={[
              'all',
              [
                'in',
                'hole_direction',
                'HORIZONTAL',
                'HORIZONTAL MINING BOREHOLE',
              ],
              [
                'any',
                ...(isHorizontalProducingEnabled
                  ? [['==', 'status', 'producing']]
                  : []),
                ...(isHorizontalDucEnabled ? [['==', 'status', 'duc']] : []),
                ...(isHorizontalPermitEnabled
                  ? [['==', 'status', 'permit']]
                  : []),
                ...(isHorizontalShutInEnabled
                  ? [['==', 'status', 'shut_in']]
                  : []),
                ...(isHorizontalExpiredPermitEnabled
                  ? [['==', 'status', 'expired_permit']]
                  : []),
                ...(isHorizontalInactiveEnabled
                  ? [['==', 'status', 'inactive']]
                  : []),
                ...statusFilters(horizontalNonDerived),
              ],
            ]}
            layout={{
              'symbol-placement': 'line',
              'text-field': [
                'concat',
                ['get', 'lease_name'],
                ' ',
                ['get', 'well_number'],
              ],
              'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10],
              'text-offset': [0, -1], // Offset text above the line
              'text-anchor': 'center',
              'text-allow-overlap': false,
              'text-ignore-placement': true,
              'symbol-spacing': 300, // Reduced spacing between repeated labels
              'visibility': 'visible',
              'symbol-z-order': 'source',
              'text-max-width': 12, // Limit text width to prevent very long names
              'text-letter-spacing': 0.05, // Slightly increase letter spacing for better readability
              'symbol-sort-key': 1, // Prioritize these labels
              'text-max-angle': 45, // Allow more angled text for better coverage
              'text-padding': 2, // Reduce padding around text
              'text-pitch-alignment': 'viewport', // Keep text aligned with the viewport
            }}
            paint={{
              'text-color': '#666666', // Gray color
              'text-halo-color': '#ffffff',
              'text-halo-width': 2,
            }}
          />

          {/* Stick lines for horizontal wells that don't have survey */}
          <Layer
            id="package-wells-v2-horizontal-stick"
            source="package-wells-v2-layer-source"
            type="line"
            source-layer="stick"
            filter={[
              'all',
              [
                'in',
                'hole_direction',
                'HORIZONTAL',
                'HORIZONTAL MINING BOREHOLE',
              ],
              ['==', 'has_survey', false],
              [
                'any',
                ...(isHorizontalProducingEnabled
                  ? [['==', 'status', 'producing']]
                  : []),
                ...(isHorizontalDucEnabled ? [['==', 'status', 'duc']] : []),
                ...(isHorizontalPermitEnabled
                  ? [['==', 'status', 'permit']]
                  : []),
                ...(isHorizontalShutInEnabled
                  ? [['==', 'status', 'shut_in']]
                  : []),
                ...(isHorizontalExpiredPermitEnabled
                  ? [['==', 'status', 'expired_permit']]
                  : []),
                ...(isHorizontalInactiveEnabled
                  ? [['==', 'status', 'inactive']]
                  : []),
                ...statusFilters(horizontalNonDerived),
              ],
            ]}
            paint={{
              'line-color': [
                'match',
                ['get', 'status'],
                'producing',
                colors.producing,
                'duc',
                colors.duc,
                'permit',
                colors.permit,
                'shut_in',
                colors.shutIn,
                'expired_permit',
                colors.expiredPermit,
                'inactive',
                colors.inactive,
                'proposal',
                colors.proposal,
                'planned',
                colors.planned,
                'pre_permit',
                colors.prePermit,
                'apd',
                colors.apd,
                '#000000', // default color if none match
              ],
              'line-width': 1.5,
              'line-dasharray': [6, 2],
            }}
          />

          {/* Well names along stick lines for horizontal wells */}
          <Layer
            id="package-wells-v2-horizontal-stick-names"
            source="package-wells-v2-layer-source"
            type="symbol"
            source-layer="stick"
            filter={[
              'all',
              [
                'in',
                'hole_direction',
                'HORIZONTAL',
                'HORIZONTAL MINING BOREHOLE',
              ],
              ['==', 'has_survey', false],
              [
                'any',
                ...(isHorizontalProducingEnabled
                  ? [['==', 'status', 'producing']]
                  : []),
                ...(isHorizontalDucEnabled ? [['==', 'status', 'duc']] : []),
                ...(isHorizontalPermitEnabled
                  ? [['==', 'status', 'permit']]
                  : []),
                ...(isHorizontalShutInEnabled
                  ? [['==', 'status', 'shut_in']]
                  : []),
                ...(isHorizontalExpiredPermitEnabled
                  ? [['==', 'status', 'expired_permit']]
                  : []),
                ...(isHorizontalInactiveEnabled
                  ? [['==', 'status', 'inactive']]
                  : []),
                ...statusFilters(horizontalNonDerived),
              ],
            ]}
            layout={{
              'symbol-placement': 'line',
              'text-field': [
                'concat',
                ['get', 'lease_name'],
                ' ',
                ['get', 'well_number'],
              ],
              'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10],
              'text-offset': [0, -1], // Offset text above the line
              'text-anchor': 'center',
              'text-allow-overlap': false,
              'text-ignore-placement': true,
              'symbol-spacing': 300, // Reduced spacing between repeated labels
              'visibility': 'visible',
              'symbol-z-order': 'source',
              'text-max-width': 12, // Limit text width to prevent very long names
              'text-letter-spacing': 0.05, // Slightly increase letter spacing for better readability
              'symbol-sort-key': 1, // Prioritize these labels
              'text-max-angle': 45, // Allow more angled text for better coverage
              'text-padding': 2, // Reduce padding around text
              'text-pitch-alignment': 'viewport', // Keep text aligned with the viewport
            }}
            paint={{
              'text-color': '#666666', // Gray color
              'text-halo-color': '#ffffff',
              'text-halo-width': 2,
            }}
          />

          {/* Surface holes */}
          {isHorizontalProducingEnabled && (
            <Layer
              id="package-wells-v2-horizontal-producing-surface"
              source="package-wells-v2-layer-source"
              type="circle"
              source-layer="surface_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['==', 'status', 'producing'],
              ]}
              paint={{
                'circle-color': colors.producing,
                'circle-radius': 4,
              }}
            />
          )}

          {isHorizontalDucEnabled && (
            <Layer
              id="package-wells-v2-horizontal-duc-surface"
              source="package-wells-v2-layer-source"
              type="circle"
              source-layer="surface_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['==', 'status', 'duc'],
              ]}
              paint={{
                'circle-color': colors.duc,
                'circle-radius': 4,
              }}
            />
          )}

          {isHorizontalPermitEnabled && (
            <Layer
              id="package-wells-v2-horizontal-permit-surface"
              source="package-wells-v2-layer-source"
              type="circle"
              source-layer="surface_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['==', 'status', 'permit'],
              ]}
              paint={{
                'circle-color': colors.permit,
                'circle-radius': 4,
              }}
            />
          )}

          {isHorizontalShutInEnabled && (
            <Layer
              id="package-wells-v2-horizontal-shut-in-surface"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="surface_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['==', 'status', 'shut_in'],
              ]}
              layout={{
                'text-field': '×',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.shutIn,
              }}
            />
          )}

          {isHorizontalExpiredPermitEnabled && (
            <Layer
              id="package-wells-v2-horizontal-expired-permit-surface"
              source="package-wells-v2-layer-source"
              type="circle"
              source-layer="surface_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['==', 'status', 'expired_permit'],
              ]}
              paint={{
                'circle-color': colors.expiredPermit,
                'circle-radius': 4,
              }}
            />
          )}

          {isHorizontalInactiveEnabled && (
            <Layer
              id="package-wells-v2-horizontal-inactive-surface"
              source="package-wells-v2-layer-source"
              type="circle"
              source-layer="surface_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['==', 'status', 'inactive'],
              ]}
              paint={{
                'circle-color': colors.inactive,
                'circle-radius': 4,
              }}
            />
          )}

          {horizontalNonDerived.length > 0 && (
            <Layer
              id="package-wells-v2-horizontal-non-derived-surface"
              type="symbol"
              source="package-wells-v2-layer-source"
              source-layer="surface_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['in', 'status', ...horizontalNonDerived],
              ]}
              layout={prePermitHexagonLayout}
              paint={prePermitHexagonPaint(PRE_PERMIT_COLOR)}
            />
          )}

          {/* Bottom holes */}
          {isHorizontalProducingEnabled && (
            <Layer
              id="package-wells-v2-horizontal-producing-bottom"
              source="package-wells-v2-layer-source"
              type="circle"
              source-layer="bottom_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['==', 'status', 'producing'],
              ]}
              paint={{
                'circle-color': colors.producing,
                'circle-radius': 4,
              }}
            />
          )}

          {isHorizontalDucEnabled && (
            <Layer
              id="package-wells-v2-horizontal-duc-bottom"
              source="package-wells-v2-layer-source"
              type="circle"
              source-layer="bottom_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['==', 'status', 'duc'],
              ]}
              paint={{
                'circle-color': colors.duc,
                'circle-radius': 4,
              }}
            />
          )}

          {isHorizontalPermitEnabled && (
            <Layer
              id="package-wells-v2-horizontal-permit-bottom"
              source="package-wells-v2-layer-source"
              type="circle"
              source-layer="bottom_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['==', 'status', 'permit'],
              ]}
              paint={{
                'circle-color': colors.permit,
                'circle-radius': 4,
              }}
            />
          )}

          {isHorizontalShutInEnabled && (
            <Layer
              id="package-wells-v2-horizontal-shut-in-bottom"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="bottom_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['==', 'status', 'shut_in'],
              ]}
              layout={{
                'text-field': '×',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.shutIn,
              }}
            />
          )}

          {isHorizontalExpiredPermitEnabled && (
            <Layer
              id="package-wells-v2-horizontal-expired-permit-bottom"
              source="package-wells-v2-layer-source"
              type="circle"
              source-layer="bottom_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['==', 'status', 'expired_permit'],
              ]}
              paint={{
                'circle-color': colors.expiredPermit,
                'circle-radius': 4,
              }}
            />
          )}

          {isHorizontalInactiveEnabled && (
            <Layer
              id="package-wells-v2-horizontal-inactive-bottom"
              source="package-wells-v2-layer-source"
              type="circle"
              source-layer="bottom_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['==', 'status', 'inactive'],
              ]}
              paint={{
                'circle-color': colors.inactive,
                'circle-radius': 4,
              }}
            />
          )}

          {horizontalNonDerived.length > 0 && (
            <Layer
              id="package-wells-v2-horizontal-non-derived-bottom"
              type="symbol"
              source="package-wells-v2-layer-source"
              source-layer="bottom_hole"
              filter={[
                'all',
                [
                  'in',
                  'hole_direction',
                  'HORIZONTAL',
                  'HORIZONTAL MINING BOREHOLE',
                ],
                ['in', 'status', ...horizontalNonDerived],
              ]}
              layout={prePermitHexagonLayout}
              paint={prePermitHexagonPaint(PRE_PERMIT_COLOR)}
            />
          )}
        </>
      )}

      {/* VERTICAL WELL LAYERS */}
      {isVerticalEnabled && (
        <>
          {/* Survey lines for vertical wells */}
          <Layer
            id="package-wells-v2-vertical-survey"
            source="package-wells-v2-layer-source"
            type="line"
            source-layer="survey"
            filter={[
              'all',
              ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
              [
                'any',
                ...(isVerticalProducingEnabled
                  ? [['==', 'status', 'producing']]
                  : []),
                ...(isVerticalDucEnabled ? [['==', 'status', 'duc']] : []),
                ...(isVerticalPermitEnabled
                  ? [['==', 'status', 'permit']]
                  : []),
                ...(isVerticalShutInEnabled
                  ? [['==', 'status', 'shut_in']]
                  : []),
                ...(isVerticalExpiredPermitEnabled
                  ? [['==', 'status', 'expired_permit']]
                  : []),
                ...(isVerticalInactiveEnabled
                  ? [['==', 'status', 'inactive']]
                  : []),
                ...statusFilters(verticalNonDerived),
              ],
            ]}
            paint={{
              'line-color': [
                'match',
                ['get', 'status'],
                'producing',
                colors.producing,
                'duc',
                colors.duc,
                'permit',
                colors.permit,
                'shut_in',
                colors.shutIn,
                'expired_permit',
                colors.expiredPermit,
                'inactive',
                colors.inactive,
                'proposal',
                colors.proposal,
                'planned',
                colors.planned,
                'pre_permit',
                colors.prePermit,
                'apd',
                colors.apd,
                '#000000', // default color if none match
              ],
              'line-width': 1.5,
            }}
          />

          {/* Well names along survey lines for vertical wells */}
          <Layer
            id="package-wells-v2-vertical-survey-names"
            source="package-wells-v2-layer-source"
            type="symbol"
            source-layer="survey"
            filter={[
              'all',
              ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
              [
                'any',
                ...(isVerticalProducingEnabled
                  ? [['==', 'status', 'producing']]
                  : []),
                ...(isVerticalDucEnabled ? [['==', 'status', 'duc']] : []),
                ...(isVerticalPermitEnabled
                  ? [['==', 'status', 'permit']]
                  : []),
                ...(isVerticalShutInEnabled
                  ? [['==', 'status', 'shut_in']]
                  : []),
                ...(isVerticalExpiredPermitEnabled
                  ? [['==', 'status', 'expired_permit']]
                  : []),
                ...(isVerticalInactiveEnabled
                  ? [['==', 'status', 'inactive']]
                  : []),
                ...statusFilters(verticalNonDerived),
              ],
            ]}
            layout={{
              'symbol-placement': 'line',
              'text-field': [
                'concat',
                ['get', 'lease_name'],
                ' ',
                ['get', 'well_number'],
              ],
              'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10],
              'text-offset': [0, -1], // Offset text above the line
              'text-anchor': 'center',
              'text-allow-overlap': false,
              'text-ignore-placement': true,
              'symbol-spacing': 300, // Reduced spacing between repeated labels
              'visibility': 'visible',
              'symbol-z-order': 'source',
              'text-max-width': 12, // Limit text width to prevent very long names
              'text-letter-spacing': 0.05, // Slightly increase letter spacing for better readability
              'symbol-sort-key': 1, // Prioritize these labels
              'text-max-angle': 45, // Allow more angled text for better coverage
              'text-padding': 2, // Reduce padding around text
              'text-pitch-alignment': 'viewport', // Keep text aligned with the viewport
            }}
            paint={{
              'text-color': '#666666', // Gray color
              'text-halo-color': '#ffffff',
              'text-halo-width': 2,
            }}
          />

          {/* Stick lines for vertical wells that don't have survey */}
          <Layer
            id="package-wells-v2-vertical-stick"
            source="package-wells-v2-layer-source"
            type="line"
            source-layer="stick"
            filter={[
              'all',
              ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
              ['==', 'has_survey', false],
              [
                'any',
                ...(isVerticalProducingEnabled
                  ? [['==', 'status', 'producing']]
                  : []),
                ...(isVerticalDucEnabled ? [['==', 'status', 'duc']] : []),
                ...(isVerticalPermitEnabled
                  ? [['==', 'status', 'permit']]
                  : []),
                ...(isVerticalShutInEnabled
                  ? [['==', 'status', 'shut_in']]
                  : []),
                ...(isVerticalExpiredPermitEnabled
                  ? [['==', 'status', 'expired_permit']]
                  : []),
                ...(isVerticalInactiveEnabled
                  ? [['==', 'status', 'inactive']]
                  : []),
                ...statusFilters(verticalNonDerived),
              ],
            ]}
            paint={{
              'line-color': [
                'match',
                ['get', 'status'],
                'producing',
                colors.producing,
                'duc',
                colors.duc,
                'permit',
                colors.permit,
                'shut_in',
                colors.shutIn,
                'expired_permit',
                colors.expiredPermit,
                'inactive',
                colors.inactive,
                'proposal',
                colors.proposal,
                'planned',
                colors.planned,
                'pre_permit',
                colors.prePermit,
                'apd',
                colors.apd,
                '#000000', // default color if none match
              ],
              'line-width': 1.5,
              'line-dasharray': [6, 2],
            }}
          />

          {/* Well names along stick lines for vertical wells */}
          <Layer
            id="package-wells-v2-vertical-stick-names"
            source="package-wells-v2-layer-source"
            type="symbol"
            source-layer="stick"
            filter={[
              'all',
              ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
              ['==', 'has_survey', false],
              [
                'any',
                ...(isVerticalProducingEnabled
                  ? [['==', 'status', 'producing']]
                  : []),
                ...(isVerticalDucEnabled ? [['==', 'status', 'duc']] : []),
                ...(isVerticalPermitEnabled
                  ? [['==', 'status', 'permit']]
                  : []),
                ...(isVerticalShutInEnabled
                  ? [['==', 'status', 'shut_in']]
                  : []),
                ...(isVerticalExpiredPermitEnabled
                  ? [['==', 'status', 'expired_permit']]
                  : []),
                ...(isVerticalInactiveEnabled
                  ? [['==', 'status', 'inactive']]
                  : []),
                ...statusFilters(verticalNonDerived),
              ],
            ]}
            layout={{
              'symbol-placement': 'line',
              'text-field': [
                'concat',
                ['get', 'lease_name'],
                ' ',
                ['get', 'well_number'],
              ],
              'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10],
              'text-offset': [0, -1], // Offset text above the line
              'text-anchor': 'center',
              'text-allow-overlap': false,
              'text-ignore-placement': true,
              'symbol-spacing': 300, // Reduced spacing between repeated labels
              'visibility': 'visible',
              'symbol-z-order': 'source',
              'text-max-width': 12, // Limit text width to prevent very long names
              'text-letter-spacing': 0.05, // Slightly increase letter spacing for better readability
              'symbol-sort-key': 1, // Prioritize these labels
              'text-max-angle': 45, // Allow more angled text for better coverage
              'text-padding': 2, // Reduce padding around text
              'text-pitch-alignment': 'viewport', // Keep text aligned with the viewport
            }}
            paint={{
              'text-color': '#666666', // Gray color
              'text-halo-color': '#ffffff',
              'text-halo-width': 2,
            }}
          />

          {/* Surface holes */}
          {isVerticalProducingEnabled && (
            <Layer
              id="package-wells-v2-vertical-producing-surface"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="surface_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['==', 'status', 'producing'],
              ]}
              layout={{
                'text-field': '●',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.producing,
              }}
            />
          )}

          {isVerticalDucEnabled && (
            <Layer
              id="package-wells-v2-vertical-duc-surface"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="surface_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['==', 'status', 'duc'],
              ]}
              layout={{
                'text-field': '●',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.duc,
              }}
            />
          )}

          {isVerticalPermitEnabled && (
            <Layer
              id="package-wells-v2-vertical-permit-surface"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="surface_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['==', 'status', 'permit'],
              ]}
              layout={{
                'text-field': '●',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.permit,
              }}
            />
          )}

          {isVerticalShutInEnabled && (
            <Layer
              id="package-wells-v2-vertical-shut-in-surface"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="surface_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['==', 'status', 'shut_in'],
              ]}
              layout={{
                'text-field': '×',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.shutIn,
              }}
            />
          )}

          {isVerticalExpiredPermitEnabled && (
            <Layer
              id="package-wells-v2-vertical-expired-permit-surface"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="surface_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['==', 'status', 'expired_permit'],
              ]}
              layout={{
                'text-field': '●',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.expiredPermit,
              }}
            />
          )}

          {isVerticalInactiveEnabled && (
            <Layer
              id="package-wells-v2-vertical-inactive-surface"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="surface_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['==', 'status', 'inactive'],
              ]}
              layout={{
                'text-field': '●',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.inactive,
              }}
            />
          )}

          {verticalNonDerived.length > 0 && (
            <Layer
              id="package-wells-v2-vertical-non-derived-surface"
              type="symbol"
              source="package-wells-v2-layer-source"
              source-layer="surface_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['in', 'status', ...verticalNonDerived],
              ]}
              layout={prePermitHexagonLayout}
              paint={prePermitHexagonPaint(PRE_PERMIT_COLOR)}
            />
          )}

          {/* Bottom holes */}
          {isVerticalProducingEnabled && (
            <Layer
              id="package-wells-v2-vertical-producing-bottom"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="bottom_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['==', 'status', 'producing'],
              ]}
              layout={{
                'text-field': '●',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.producing,
              }}
            />
          )}

          {isVerticalDucEnabled && (
            <Layer
              id="package-wells-v2-vertical-duc-bottom"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="bottom_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['==', 'status', 'duc'],
              ]}
              layout={{
                'text-field': '●',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.duc,
              }}
            />
          )}

          {isVerticalPermitEnabled && (
            <Layer
              id="package-wells-v2-vertical-permit-bottom"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="bottom_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['==', 'status', 'permit'],
              ]}
              layout={{
                'text-field': '●',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.permit,
              }}
            />
          )}

          {isVerticalShutInEnabled && (
            <Layer
              id="package-wells-v2-vertical-shut-in-bottom"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="bottom_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['==', 'status', 'shut_in'],
              ]}
              layout={{
                'text-field': '×',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.shutIn,
              }}
            />
          )}

          {isVerticalExpiredPermitEnabled && (
            <Layer
              id="package-wells-v2-vertical-expired-permit-bottom"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="bottom_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['==', 'status', 'expired_permit'],
              ]}
              layout={{
                'text-field': '●',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.expiredPermit,
              }}
            />
          )}

          {isVerticalInactiveEnabled && (
            <Layer
              id="package-wells-v2-vertical-inactive-bottom"
              source="package-wells-v2-layer-source"
              type="symbol"
              source-layer="bottom_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['==', 'status', 'inactive'],
              ]}
              layout={{
                'text-field': '●',
                'text-allow-overlap': true,
                'text-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  1,
                  10,
                  20,
                  16,
                ],
              }}
              paint={{
                'text-color': colors.inactive,
              }}
            />
          )}

          {verticalNonDerived.length > 0 && (
            <Layer
              id="package-wells-v2-vertical-non-derived-bottom"
              type="symbol"
              source="package-wells-v2-layer-source"
              source-layer="bottom_hole"
              filter={[
                'all',
                ['in', 'hole_direction', 'VERTICAL', 'DIRECTIONAL', 'PINNATE'],
                ['in', 'status', ...verticalNonDerived],
              ]}
              layout={prePermitHexagonLayout}
              paint={prePermitHexagonPaint(PRE_PERMIT_COLOR)}
            />
          )}
        </>
      )}
    </Source>
  );
};

export { ScopedPackageWellsV2Layer };
